import { useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { askRayGuardAi, sendRayGuardAiFeedback, sendRayGuardAiFeedbackToHub } from '../../actions/AiSearchAction';
import { Translate as t } from '../../config/Translate';
import { parseSimpleMarkdown } from '../../lib/simpleMarkdown';

export const MAX_QUESTION_LENGTH = 1000;
export const MAX_FEEDBACK_LENGTH = 1000;

const ERROR_KEYS = {
	forbidden: 'ai.message.forbidden',
	limit: 'ai.message.dailyLimit',
	error: 'ai.message.error',
};

// 안내 블록의 섹션 구성 (문구는 모두 i18n 키)
const INTRO_SECTIONS = [
	{ title: 'ai.intro.can.title', items: ['ai.intro.can.1', 'ai.intro.can.2', 'ai.intro.can.3'] },
	{ title: 'ai.intro.cannot.title', items: ['ai.intro.cannot.1', 'ai.intro.cannot.2', 'ai.intro.cannot.3'] },
	{ title: 'ai.intro.caution.title', items: ['ai.intro.caution.1', 'ai.intro.caution.2', 'ai.intro.caution.3'] },
];

// 모달 전용 스타일 (상단 정렬 + 넓은 폭). Bootstrap .modal-dialog max-width 보다 우선하도록 선택자를 겹친다.
const AI_CSS = `
.modal-dialog.rg-ai-dialog { max-width: min(900px, 94vw); width: 94vw; margin: 10vh auto 0; }
.rg-ai-input { border: 0; border-bottom: 1px solid #e3e6ea; border-radius: 0; box-shadow: none !important; font-size: 17px; height: 56px; padding: 0 8px; background: transparent; }
.rg-ai-input:focus { border-bottom-color: #6f5cff; }
.rg-ai-answer { font-size: 14px; line-height: 1.6; color: #343f52; word-break: break-word; }
.rg-ai-answer p { margin: 0 0 10px; }
.rg-ai-answer ul, .rg-ai-answer ol { margin: 0 0 10px; padding-left: 22px; }
.rg-ai-answer li { margin-bottom: 4px; }
.rg-ai-fb-btn { font-size: 13px; padding: 2px 10px; border-color: #dfe3e8; }
`;

const renderInline = (tokens) =>
	(tokens ?? []).map((tk, i) => (tk?.bold ? <strong key={i}>{tk?.text}</strong> : <span key={i}>{tk?.text}</span>));

// 마크다운 토큰을 React 요소로 직접 렌더 — dangerouslySetInnerHTML 미사용
const AnswerView = ({ text }) => {
	const blocks = parseSimpleMarkdown(text);
	return (
		<div className="rg-ai-answer" data-testid="ai-answer">
			{blocks.map((b, i) => {
				if (b?.type === 'ul') return <ul key={i}>{(b?.items ?? []).map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ul>;
				if (b?.type === 'ol') return <ol key={i}>{(b?.items ?? []).map((it, j) => <li key={j}>{renderInline(it)}</li>)}</ol>;
				return (
					<p key={i}>
						{(b?.lines ?? []).map((ln, j) => (
							<span key={j}>{j > 0 && <br />}{renderInline(ln)}</span>
						))}
					</p>
				);
			})}
		</div>
	);
};

// FEAT-061: 헤더 트리거 + 상단 정렬 검색 모달. 답변은 escape 된 React 요소로만 렌더, 근거는 표시하지 않는다.
const RayGuardAiSearch = ({ user } = {}) => {
	const [input, setInput] = useState('');
	const [show, setShow] = useState(false);
	const [asked, setAsked] = useState(false);
	const [loading, setLoading] = useState(false);
	const [answer, setAnswer] = useState('');
	const [logId, setLogId] = useState(null);
	const [askedQuestion, setAskedQuestion] = useState('');
	const [errorKey, setErrorKey] = useState('');
	const [hint, setHint] = useState('');
	const [introOpen, setIntroOpen] = useState(false);
	const [rating, setRating] = useState('');
	const [comment, setComment] = useState('');
	const [fbStatus, setFbStatus] = useState('');
	const [fbSaving, setFbSaving] = useState(false);
	const busyRef = useRef(false);
	const inputRef = useRef(null);

	const resetResult = () => {
		setAsked(false);
		setAnswer('');
		setLogId(null);
		setAskedQuestion('');
		setErrorKey('');
		setHint('');
		setRating('');
		setComment('');
		setFbStatus('');
	};

	const openModal = (draft = '') => {
		// 응답 대기 중에 닫았다 다시 열면 결과를 지우지 않고 로딩/도착한 답변을 그대로 보여 준다.
		if (busyRef.current) {
			setShow(true);
			return;
		}
		resetResult();
		setIntroOpen(false);
		setInput(draft);
		setShow(true);
	};

	const submit = async () => {
		if (busyRef.current) return;
		const q = (input ?? '').trim();
		if (!q) {
			setHint(t('ai.message.empty'));
			return;
		}
		if (q.length > MAX_QUESTION_LENGTH) {
			setHint(t('ai.message.tooLong'));
			return;
		}
		resetResult();
		setAskedQuestion(q);
		busyRef.current = true;
		setAsked(true);
		setLoading(true);
		try {
			const res = await askRayGuardAi(q);
			if (res?.ok) {
				setAnswer(res?.answer ?? '');
				setLogId(res?.logId ?? null);
			} else {
				setErrorKey(ERROR_KEYS[res?.code] || ERROR_KEYS.error);
			}
		} catch (e) {
			console.warn('[RayGuardAiSearch] ask failed', { reason: e?.message });
			setErrorKey(ERROR_KEYS.error);
		} finally {
			busyRef.current = false;
			setLoading(false);
		}
	};

	const saveFeedback = async () => {
		if (fbSaving || !rating || !logId) return;
		setFbSaving(true);
		setFbStatus('');
		try {
			const trimmed = (comment ?? '').trim().slice(0, MAX_FEEDBACK_LENGTH);
			const res = await sendRayGuardAiFeedback(logId, rating, trimmed);
			setFbStatus(res?.ok ? 'saved' : 'error');
			if (res?.ok) {
				// 피드백 허브 전송: await 하지 않음(reject 없음). 결과는 화면에 반영하지 않고 action 의 warn 으로 관측한다.
				sendRayGuardAiFeedbackToHub({ logId, rating, comment: trimmed, question: askedQuestion, answer, user });
			}
		} catch (e) {
			console.warn('[RayGuardAiSearch] feedback failed', { reason: e?.message });
			setFbStatus('error');
		} finally {
			setFbSaving(false);
		}
	};

	const onTriggerKeyDown = (e) => {
		// 한글 등 IME 조합 중에는 글자를 넘기지 않고 모달만 연다(입력칸 포커스 후 사용자가 다시 입력).
		if (e.key === 'Process' || e.nativeEvent?.isComposing || e.keyCode === 229) {
			e.preventDefault();
			openModal();
			return;
		}
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			openModal();
		} else if (e.key?.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
			// 헤더에서 바로 타이핑하면 그 글자를 모달 입력칸으로 넘긴다.
			e.preventDefault();
			openModal(e.key);
		}
	};

	const onModalKeyDown = (e) => {
		if (e.key === 'Enter' && !e.nativeEvent?.isComposing) {
			e.preventDefault();
			submit();
		}
	};

	const fbBtnClass = (r) => 'btn btn-sm mt-0 rg-ai-fb-btn ' + (rating === r ? 'btn-secondary' : 'btn-outline-secondary');

	return (
		<>
			<style>{AI_CSS}</style>
			<div className="d-none d-lg-flex align-items-center ms-2" style={{ width: 260 }}>
				<input
					type="text"
					readOnly
					className="form-control form-control-sm"
					style={{ height: 36, cursor: 'pointer', backgroundColor: '#fff' }}
					placeholder={t('ai.placeholder.ask')}
					aria-label="RAYGuard AI"
					data-testid="ai-trigger"
						aria-haspopup="dialog"
					value=""
					onClick={() => openModal()}
					onKeyDown={onTriggerKeyDown}
				/>
			</div>
			<Modal
				show={show}
				onHide={() => setShow(false)}
				dialogClassName="rg-ai-dialog"
				onEntered={() => inputRef.current?.focus()}
			>
				<Modal.Header className="py-2">
					<h5 className="modal-title d-flex align-items-center">
						RAYGuard AI
						<span className="badge ms-2" style={{ background: '#ece9ff', color: '#6f5cff', fontSize: 11, fontWeight: 600 }}>Beta</span>
					</h5>
					<button type="button" className="btn btn-link p-0 mt-0" onClick={() => setIntroOpen(!introOpen)} aria-expanded={introOpen} aria-label={t('ai.button.guide')} title={t('ai.button.guide')}>
						<ion-icon name="information-circle-outline" style={{ fontSize: 24 }}></ion-icon>
					</button>
					<button type="button" className="btn-close ms-2" onClick={() => setShow(false)} aria-label={t('ai.button.close')} title={t('ai.button.close')}></button>
				</Modal.Header>
				<Modal.Body className="px-4 pt-3 pb-4">
					{introOpen && (
						<div className="border rounded p-3 mb-3" style={{ fontSize: 13 }} data-testid="ai-intro">
							<div className="fw-bold mb-1">{t('ai.intro.title')}</div>
							<div className="mb-2">{t('ai.intro.notice')}</div>
							{INTRO_SECTIONS.map((sec) => (
								<div key={sec.title} className="mb-2">
									<div className="fw-bold">{t(sec.title)}</div>
									<ul className="mb-0 ps-3">
										{sec.items.map((k) => <li key={k}>{t(k)}</li>)}
									</ul>
								</div>
							))}
							<div>{t('ai.intro.feedback')}</div>
						</div>
					)}
					{/* 질문은 Enter 로만 보낸다(전송 버튼 없음) */}
					<input
						ref={inputRef}
						type="text"
						autoFocus
						className="form-control rg-ai-input"
						placeholder={t('ai.placeholder.ask')}
						aria-label={t('ai.placeholder.ask')}
						data-testid="ai-modal-input"
						value={input}
						onChange={(e) => { setInput(e.target.value); setHint(''); }}
						onKeyDown={onModalKeyDown}
					/>
					{hint && <div className="text-danger mt-1" style={{ fontSize: 12 }} role="alert">{hint}</div>}
					{asked && (
						<div className="mt-3">
							{loading && (
								<div className="d-flex align-items-center my-3" data-testid="ai-loading">
									<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									<span style={{ fontSize: 14 }}>{t('ai.message.loading')}</span>
								</div>
							)}
							{!loading && errorKey && <div className="alert alert-danger mb-0" role="alert" data-testid="ai-error">{t(errorKey)}</div>}
							{!loading && !errorKey && answer && (
								<>
									<AnswerView text={answer} />
									{logId && (
										<div className="mt-3" data-testid="ai-feedback">
											<div className="d-flex gap-2">
												<button type="button" className={fbBtnClass('up')} aria-pressed={rating === 'up'} disabled={fbSaving || fbStatus === 'saved'} onClick={() => setRating('up')}>{'👍'} {t('ai.button.up')}</button>
												<button type="button" className={fbBtnClass('down')} aria-pressed={rating === 'down'} disabled={fbSaving || fbStatus === 'saved'} onClick={() => setRating('down')}>{'👎'} {t('ai.button.down')}</button>
											</div>
											{rating && fbStatus !== 'saved' && (
												<div className="d-flex gap-2 mt-2">
													<input type="text" className="form-control form-control-sm" maxLength={MAX_FEEDBACK_LENGTH} placeholder={t('ai.placeholder.feedback')} value={comment} onChange={(e) => setComment(e.target.value)} />
													<button type="button" className="btn btn-sm btn-primary mt-0 text-nowrap" disabled={fbSaving} onClick={saveFeedback}>{t('ai.button.saveFeedback')}</button>
												</div>
											)}
											{fbStatus === 'saved' && <div className="text-success mt-2" style={{ fontSize: 13 }} role="status" data-testid="ai-feedback-saved">{t('ai.message.feedbackSaved')}</div>}
											{fbStatus === 'error' && <div className="text-danger mt-2" style={{ fontSize: 13 }} role="alert" data-testid="ai-feedback-error">{t('ai.message.feedbackError')}</div>}
										</div>
									)}
								</>
							)}
							<div className="text-muted mt-3" style={{ fontSize: 12 }}>{t('ai.message.disclaimer')}</div>
						</div>
					)}
				</Modal.Body>
			</Modal>
		</>
	);
};

export default RayGuardAiSearch;
